import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import * as Scroll from 'react-scroll';

import { DomHelpers, Forms, Modal, SaveButton, SystemText } from 'e4shared';

import { setShowSuccess, toggleFormHelp } from 'actions/ui';
import { endOfDay, isPast } from 'helpers/dates';
import { apiGet, apiPatch } from 'services';
import Banner from 'components/general/banner';
import { setDashboardCollection } from 'actions/dashboard';

export const ScheduleEdit = ({ show, close, onSchedule, users }) => {
    const dispatch = useDispatch();

    const { user } = useSelector(state => state.user);
    const { teams } = useSelector(state => state.dashboard);

    const [saving, setSaving] = useState(false);
    const [managerWarning, setManagerWarning] = useState('');
    const [dateWarning, setDateWarning] = useState('');
    const [errors, setErrors] = useState({});
    const [managers, setManagers] = useState([]);
    const [selected, setSelected] = useState([]);
    const [values, setValues] = useState({});

    React.useEffect(() => {
        if (!teams?.length) {
            apiGet('dashboard-teams', user.guid, response => dispatch(setDashboardCollection('teams', response)));
        } else {
            setManagers(teams);
        }
    }, [teams]);

    useEffect(() => {
        const temp = { ...values };

        if (show?.length) {
            temp.ids = show.map(x => x.profile_Id);

            const first = show.find(x => x);
            temp.manager_Id = first?.appraisal_Manager_Id;
            temp.date = first?.scheduled;

            if (show.length > 1) {
                const multiple = SystemText('ReviewTracker/Schedule/Fields/General/Placeholder/Multiple');

                if (show.some(x => x.appraisal_Manager_Id !== temp.manager_Id)) {
                    setManagerWarning(multiple);
                    temp.manager_Id = 0;
                }

                if (show.some(x => x.scheduled !== temp.date)) {
                    setDateWarning(multiple);
                    temp.date = false; // Set to false to blank out in react-datecontrol
                }
            }
        }

        setValues(temp);
    }, [show]);

    useEffect(() => {
        if (values?.ids) {
            const temp = users.filter(u => values.ids.some(x => x === u.id));
            setSelected([...temp]);
        }
    }, [values?.ids]);

    const validate = React.useCallback(() => {
        const review = { ...values };
        let result = true;
        const theseErrors = {};

        const allManagers = show.reduce((a, c) => {
            return a.some(x => x === c.manager_Id) ? a : a.concat([c.manager_Id]);
        }, []);

        if (!review.manager_Id) {
            theseErrors.manager_Id = SystemText('ReviewTracker/Schedule/Fields/Manager/Errors/Mandatory');
            result = false;
        } else if (allManagers.length > 1) {
            theseErrors.main = SystemText('ReviewTracker/Schedule/Fields/Manager/Errors/Share');
            result = false;
        } else if (show.some(x => x.manager_Id !== review.manager_Id)) {
            theseErrors.main = SystemText('ReviewTracker/Schedule/Fields/Manager/Errors/Associated');
            result = false;
        }

        if (!review?.date || review?.date?.length === 0 || review?.date === '1970-01-01') {
            theseErrors.date = SystemText('ReviewTracker/Schedule/Fields/Date/Error');
            result = false;
        } else if (isPast(endOfDay(review.date))) {
            theseErrors.date = SystemText('ReviewTracker/Schedule/Fields/Date/Error/Past');
            result = false;
        }

        setErrors(theseErrors);

        return result;
    }, [values, show]);

    const closeModal = () => {
        setSaving(false);
        setValues({});
        setErrors({});
        close?.();
    };

    const save = () => {
        if (validate()) {
            apiPatch('review-reassign', values, user.guid, () => {
                dispatch(setShowSuccess(SystemText('ReviewTracker/Schedule/Reassign/Success')));
                onSchedule?.();
                closeModal();
                setSaving(false);
            });
        } else {
            setSaving(false);
        }
    };

    const setValue = (property, value) => {
        const temp = { ...values };
        temp[property] = value;
        setValues(temp);
    };

    const userList = React.useCallback(() => {
        if (!values?.ids) return '-';
        const names = [...selected].slice(0, 10).reduce((a, c, i) => `${a}${i > 0 ? ', ' : ''}${c.full_Name}`, '');
        const extra = SystemText('ReviewTracker/Schedule/Fields/Users/Extra', selected.length - 10);
        return selected.length > 10 ? `${names} ${extra}` : names;
    }, [values?.ids, selected]);

    const buttonClick = React.useCallback(
        e => {
            const btn = DomHelpers.getEventButton(e);
            switch (btn.value) {
                case 'save':
                    setSaving(true);
                    save();
                    break;
                case 'close':
                    closeModal();
                    break;
                default:
                    break;
            }
        },
        [validate]
    );

    const footer = (
        <div>
            <SaveButton
                data-testid="post-save-button"
                saving={saving}
                setSaving={setSaving}
                className="btn btn-success"
                value="save"
                onClick={buttonClick}
            >
                {SystemText('ReviewTracker/Schedule/Buttons/Save')}
            </SaveButton>
            <button className="btn" value="close" onClick={buttonClick} data-testid="button-post-cancel">
                {SystemText('ReviewTracker/Schedule/Buttons/Cancel')}
            </button>
        </div>
    );

    return (
        <Modal
            data-testid="post-modal"
            show={show}
            title={SystemText('ReviewTracker/Schedule/Title/Edit')}
            close={closeModal}
            footer={footer}
            toggleFormHelp={() => dispatch(toggleFormHelp())}
        >
            <Scroll.Element name="top-of-modal" />
            <div className="form-group" id="manage-task-form">
                {errors.main && (
                    <div className="pb10">
                        <Banner colour="danger" icon="warning" message={errors.main} />
                    </div>
                )}
                <Forms.Label
                    label={SystemText('ReviewTracker/Schedule/Fields/Reviewee/Label')}
                    key={`usernames-${(values?.ids || []).join(',')}-${selected.length}`}
                    value={userList}
                />
                <Forms.Select
                    data-testid="field-managers"
                    value={values.manager_Id}
                    placeholder={managerWarning || SystemText('ReviewTracker/Schedule/Fields/Reviewer/Placeholder')}
                    disabled
                    mandatory
                    data={managers?.map(x => ({ id: x.id, name: x.list_Name })) || []}
                    onChange={value => setValue('manager_Id', value)}
                    label={SystemText('ReviewTracker/Schedule/Fields/Reviewer/Label')}
                    error={errors.manager_Id}
                />
                <Forms.Date
                    data-testid="field-review-date"
                    value={values.date}
                    placeholder={dateWarning}
                    help={SystemText('ReviewTracker/Schedule/Fields/Date/Help')}
                    onChange={value => setValue('date', value)}
                    label={SystemText('ReviewTracker/Schedule/Fields/Date/Label')}
                    mandatory
                    bypassDefault={dateWarning ? true : false}
                    error={errors.date}
                />
            </div>
        </Modal>
    );
};

export default ScheduleEdit;
